import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Modal } from "antd"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { setSlides, setActiveSlide } from "store/lightbox"
import { Text } from "ui/Typography"
import { numberToCurrency } from "helpers/string"
import styles from "./Form.module.css"

export default function DuplicateExpenseModal({ open, onCancel, onSave, isSaving, duplicates }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const duplicateSlides = useMemo(
    () =>
      duplicates.flatMap((dup) =>
        (dup.receipts || []).filter((r) => r.full_url || r.url).map((r) => ({ type: "image", url: r.full_url || r.url, userId: r.id }))
      ),
    [duplicates]
  )

  const receiptSlideIndexMap = useMemo(
    () => Object.fromEntries(duplicateSlides.map((s, i) => [s.userId, i])),
    [duplicateSlides]
  )

  useEffect(() => {
    if (!open) return
    dispatch(setSlides(duplicateSlides))
    return () => dispatch(setSlides([]))
  }, [open, duplicateSlides, dispatch])

  return (
    <Modal
      open={open}
      title={t("expense.duplicate_modal.title")}
      onCancel={onCancel}
      maskClosable={false}
      zIndex={1000}
      footer={
        <Flex justify="end" gap={8}>
          <Button onClick={onCancel}>{t("global.cancel")}</Button>
          <Button color="primary" variant="solid" loading={isSaving} onClick={onSave}>
            {t("expense.duplicate_modal.save_anyway")}
          </Button>
        </Flex>
      }
      width={600}
    >
      <Flex vertical gap={12} className="py-s">
        <Text>{t("expense.duplicate_modal.description")}</Text>
        <div className={styles.duplicateCardWrap}>
          {duplicates.map((dup) => (
            <div key={dup.id} className={styles.duplicateCard}>
              <Flex gap={12} align="start">
                {dup.receipts?.length > 0 && dup.receipts[0]?.url && (
                  <button
                    type="button"
                    className={styles.duplicateReceiptThumbBtn}
                    onClick={() => {
                      const idx = receiptSlideIndexMap[dup.receipts[0].id]
                      if (idx !== undefined) dispatch(setActiveSlide(idx))
                    }}
                  >
                    <img src={dup.receipts[0].url} alt="receipt" className={styles.duplicateReceiptThumb} />
                  </button>
                )}
                <Flex vertical gap={4} style={{ flex: 1 }}>
                  <Flex justify="space-between" align="center">
                    <Text fw="600">{dup.merchant_name}</Text>
                    <Text fw="600">{numberToCurrency(dup.amount)}</Text>
                  </Flex>
                  <Text className="text-dark text-opacity-50">{dup.purchase_date}</Text>
                  {dup.notes && <Text>{dup.notes}</Text>}
                  {dup.expense_line_items?.length > 0 && (
                    <Flex vertical gap={2} className="mt-xxs">
                      {dup.expense_line_items.map((li, i) => (
                        <Flex key={li.id ?? i} justify="space-between" gap={10}>
                          <Text className="text-dark text-opacity-50 break-all">{li.name}</Text>
                          <Text className="text-dark text-opacity-50 white-space-nowrap">${li.amount}</Text>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </div>
          ))}
        </div>
      </Flex>
    </Modal>
  )
}

DuplicateExpenseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  duplicates: PropTypes.array.isRequired
}
